import {
  Accordion,
  AccordionTypes,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Actionsheet, Pressable, useDisclose } from 'native-base';
import React, { useMemo, useState } from 'react';

export interface TermsAndDocumentsItem {
  title: string;
  onPress?: () => void;
  url?: string;
}

interface TermsAndDocumentsProps {
  items: TermsAndDocumentsItem[];
  triggerLabel?: string;
}

export const TermsAndDocuments: React.FC<TermsAndDocumentsProps> = ({
  items,
  triggerLabel = 'TERMOS E DOCUMENTOS',
}) => {
  const isDesktop = useBreakpointValue({ base: false, sm: true });
  const [accordionState, setAccordionState] = useState<AccordionTypes.Status>('closed');
  const { isOpen, onOpen, onClose } = useDisclose();

  const hasItems = items.length > 0;

  const openLink = (item: TermsAndDocumentsItem) => {
    if (item.onPress) {
      item.onPress();
      return;
    }
    if (item.url) {
      window.open(item.url, '_blank');
    }
  };

  const documentItems = useMemo(
    () =>
      items.map((item, index) => (
        <Pressable
          key={`${item.title}-${index}`}
          onPress={() => openLink(item)}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center">
          <Text fontSize="md" color="brand.primary.darkest">
            {item.title}
          </Text>
          <IconButton
            icon="Declaration"
            iconSize="xs"
            iconColor={Color['brand.tertiary.principal']}
            p={0}
            onPress={() => openLink(item)}
            isDisabled={!item.url}
            testId={`terms-document-${index}`}
          />
        </Pressable>
      )),
    [items]
  );

  const triggerButton = (
    <Pressable
      onPress={
        isDesktop
          ? () => setAccordionState((prev) => (prev === 'closed' ? 'opened' : 'closed'))
          : onOpen
      }
      justifyContent="space-between"
      alignItems="center"
      flexDirection="row"
      w="full"
      py={Spacing['stack.nano']}>
      <Text color="brand.tertiary.principal" variant="semibold" fontSize="md">
        {triggerLabel}
      </Text>
      <Icon name="ChevronRight" size="xxs" color="brand.tertiary.principal" />
    </Pressable>
  );

  if (isDesktop) {
    return (
      <Accordion
        status={accordionState}
        onPress={() => setAccordionState((prev) => (prev === 'closed' ? 'opened' : 'closed'))}
        hasShadow={false}
        containerProps={{ w: 'full', bg: 'neutral.white', p: 0 }}
        forceDesktopLayout>
        <Accordion.Visible w="full" flex={1} hideDefaultIcons>
          {triggerButton}
        </Accordion.Visible>

        <Accordion.Expanded forceDesktopLayout>
          <VStack
            borderWidth={1}
            borderColor="neutral.lightest"
            borderRadius={BoxRadius.sm}
            px={Spacing['inline.xxxs']}
            py={Spacing['inline.nano']}
            space={Spacing['stack.nano']}>
            <Text variant="semibold" color="brand.primary.darkest">
              Selecione o documento
            </Text>
            {hasItems ? (
              documentItems
            ) : (
              <Text color="neutral.medium">Nenhum documento disponível.</Text>
            )}
          </VStack>
        </Accordion.Expanded>
      </Accordion>
    );
  }

  return (
    <>
      {triggerButton}

      <Actionsheet useRNModal isOpen={isOpen} onClose={onClose} size="full">
        <Actionsheet.Content _dragIndicator={{ bg: 'neutral.lightest' }} w="full">
          <VStack
            py={Spacing['inline.xxs']}
            px={Spacing['inline.nano']}
            w="full"
            space="stack.xxxs">
            <HStack justifyContent="space-between" w="full" alignItems="center">
              <Text fontSize="lg" color="brand.primary.darkest" variant="semibold">
                Selecione o documento
              </Text>
              <IconButton
                onPress={onClose}
                icon="Close"
                iconSize="xxxs"
                iconColor={Color['neutral.medium']}
                testId="terms-documents-close-mobile"
                p={0}
              />
            </HStack>

            {hasItems ? (
              documentItems
            ) : (
              <Text color="neutral.medium">Nenhum documento disponível.</Text>
            )}
          </VStack>
        </Actionsheet.Content>
      </Actionsheet>
    </>
  );
};
